/**
 * "Name-starting" characters — letters (any script) and digits. Excludes
 * emojis, punctuation, symbols, and whitespace. Shared between the Settings
 * nickname editor (for validation) and the avatar-initial helper so the two
 * can't drift: if a nickname wouldn't produce an avatar initial, we don't
 * let it save in the first place.
 */
// Matches a "letter-like" glyph: a letter or digit, plus any following
// combining marks. The \p{M}* tail matters for abugida scripts
// (Baybayin, Devanagari, Tamil, Khmer, etc.) where a base consonant
// letter carries an inherent vowel and the actual syllable is formed
// by a combining mark (kudlit, matra, etc.) that follows. Without the
// tail, "ᜎᜒ" (Baybayin "li") would render as "ᜎ" ("la") in the
// avatar — same problem for Devanagari "कि" etc.
const NAME_STARTING_CHAR = /[\p{L}\p{N}]\p{M}*/u;

export const hasNameStartingChar = (str: string): boolean => NAME_STARTING_CHAR.test(str);

export const getFirstNameStartingChar = (str: string): string | null => {
  const match = str.match(NAME_STARTING_CHAR);
  return match ? match[0] : null;
};

export type NicknameFormState = {
  trimmed: string;
  isValid: boolean;
  isUnchanged: boolean;
  canSubmit: boolean;
};

/**
 * Shared state derivation for any "change nickname" form (today only the
 * Settings Account tab). Keeps every such form agreeing on what counts as
 * submittable.
 *
 * - trimmed: whitespace-stripped current value
 * - isValid: has at least one name-starting character
 * - isUnchanged: matches the stored nickname (trimmed)
 * - canSubmit: enabled state for the submit button
 */
export const getNicknameFormState = (
  current: string,
  stored: string,
  loading = false,
): NicknameFormState => {
  const trimmed = current.trim();
  const isValid = hasNameStartingChar(trimmed);
  const isUnchanged = trimmed === stored.trim();
  const canSubmit = !loading && isValid && !isUnchanged;
  return { trimmed, isValid, isUnchanged, canSubmit };
};
